import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, ShieldCheck, ShieldQuestion } from "lucide-react";
import {
  PROFILE_BY_LEVEL,
  riskStatus,
  minRiskStatus,
  trackingError,
  maxDrawdown,
  type RiskStatus,
} from "@/lib/risk-policy";

interface Props { range: 'P' | 'R' }

interface DailyValue {
  portfolio_level: number;
  series_type: 'portfolio' | 'benchmark';
  value_date: string;
  value: number;
}

interface PolicyCeiling {
  profile_level: number;
  var95_ceiling_pct: number;
  tracking_error_ceiling_pct: number;
  max_drawdown_ceiling_pct: number;
  liquidity_t7_min_pct: number;
}

interface FundLiquidity {
  fund_isin: string | null;
  t1_pct: number | null;
  t7_pct: number | null;
  t30_pct: number | null;
}

interface FundWeight {
  fund_id: string;
  portfolio: number;
  weight_pct: number;
  effective_date: string;
}

interface FundLite { id: string; isin: string | null; is_current: boolean }

async function fetchAllDaily(range: 'P' | 'R'): Promise<DailyValue[]> {
  const all: DailyValue[] = [];
  const pageSize = 1000;
  let from = 0;
  while (true) {
    const { data, error } = await supabase
      .from('risk_daily_values')
      .select('portfolio_level, series_type, value_date, value')
      .eq('portfolio_range', range)
      .order('value_date', { ascending: true })
      .range(from, from + pageSize - 1);
    if (error) throw error;
    if (!data || data.length === 0) break;
    all.push(...(data as DailyValue[]));
    if (data.length < pageSize) break;
    from += pageSize;
  }
  return all;
}

export default function RiskLensesPanel({ range }: Props) {
  const { data: daily = [] } = useQuery({
    queryKey: ['risk-daily', range],
    queryFn: () => fetchAllDaily(range),
  });

  const { data: ceilings = [] } = useQuery({
    queryKey: ['policy-risk-ceilings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('policy_risk_ceilings' as any)
        .select('*')
        .order('profile_level');
      if (error) throw error;
      return (data ?? []) as unknown as PolicyCeiling[];
    },
  });

  const { data: liquidity = [] } = useQuery({
    queryKey: ['risk-fund-liquidity'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('risk_fund_liquidity' as any)
        .select('fund_isin, t1_pct, t7_pct, t30_pct');
      if (error) throw error;
      return (data ?? []) as unknown as FundLiquidity[];
    },
  });

  const { data: weights = [] } = useQuery({
    queryKey: ['ba-cbam-fund-weights'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ba_cbam_fund_weights')
        .select('fund_id, portfolio, weight_pct, effective_date');
      if (error) throw error;
      return data as FundWeight[];
    },
  });

  const { data: funds = [] } = useQuery({
    queryKey: ['ba-cbam-funds-lite'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ba_cbam_funds')
        .select('id, isin, is_current');
      if (error) throw error;
      return data as FundLite[];
    },
  });

  const ceilingByLevel = useMemo(() => {
    const m = new Map<number, PolicyCeiling>();
    for (const c of ceilings) m.set(c.profile_level, c);
    return m;
  }, [ceilings]);

  const liquidityByIsin = useMemo(() => {
    const m = new Map<string, FundLiquidity>();
    for (const l of liquidity) if (l.fund_isin) m.set(l.fund_isin.trim().toUpperCase(), l);
    return m;
  }, [liquidity]);

  const fundIsinById = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const f of funds) if (f.is_current) m.set(f.id, f.isin?.trim().toUpperCase() ?? null);
    return m;
  }, [funds]);

  // Latest weights snapshot per portfolio level. Resolve the latest row per (fund, portfolio)
  // rather than per portfolio: the table is effective-dated per fund, so one fund trading (or
  // exiting, which writes an explicit weight_pct = 0 row at the exit date) must not hide every
  // other fund whose newest row is older. Zero rows are dropped — they are exits, not holdings.
  const latestWeightsByLevel = useMemo(() => {
    const latest = new Map<string, FundWeight>();
    for (const w of weights) {
      const key = `${w.fund_id}-${w.portfolio}`;
      const prev = latest.get(key);
      if (!prev || w.effective_date > prev.effective_date) latest.set(key, w);
    }
    const m = new Map<number, FundWeight[]>();
    for (const w of latest.values()) {
      if (Number(w.weight_pct) <= 0) continue;
      const arr = m.get(w.portfolio) ?? [];
      arr.push(w);
      m.set(w.portfolio, arr);
    }
    return m;
  }, [weights]);

  const rows = useMemo(() => {
    return [1, 2, 3, 4, 5].map(level => {
      const profile = PROFILE_BY_LEVEL[level];
      const c = ceilingByLevel.get(level);

      // Tracking Error & Max DD from daily series. Align portfolio & benchmark by value_date BEFORE
      // differencing — pairing by array position is wrong whenever the two series have any missing /
      // extra dates, which fabricates the annualised TE.
      const portByDate = new Map<string, number>();
      const benchByDate = new Map<string, number>();
      for (const d of daily) {
        if (d.portfolio_level !== level) continue;
        if (d.series_type === 'portfolio') portByDate.set(d.value_date, Number(d.value));
        else if (d.series_type === 'benchmark') benchByDate.set(d.value_date, Number(d.value));
      }
      const portDates = Array.from(portByDate.keys()).sort();
      const portSeries = portDates.map(dt => portByDate.get(dt)!); // date-ordered, for MaxDD
      const commonDates = portDates.filter(dt => benchByDate.has(dt)); // intersection, already sorted

      const teWindow = 365;
      const teDates = commonDates.slice(-teWindow - 1);
      const pTail = teDates.map(dt => portByDate.get(dt)!);
      const bTail = teDates.map(dt => benchByDate.get(dt)!);
      const te = trackingError(pTail, bTail);
      const mdd = maxDrawdown(portSeries);

      // Weighted T+7 liquidity coverage
      const wts = latestWeightsByLevel.get(level) ?? [];
      let totalWeight = 0;
      let weightedLiq = 0;
      let unmappedWeight = 0;
      for (const w of wts) {
        const isin = fundIsinById.get(w.fund_id);
        totalWeight += Number(w.weight_pct);
        const liq = isin ? liquidityByIsin.get(isin) : null;
        if (liq?.t7_pct != null) {
          weightedLiq += Number(w.weight_pct) * Number(liq.t7_pct);
        } else {
          unmappedWeight += Number(w.weight_pct);
        }
      }
      const liqCoverage = totalWeight > 0 ? weightedLiq / totalWeight : null;

      const teCeiling = c?.tracking_error_ceiling_pct ?? null;
      const mddCeiling = c?.max_drawdown_ceiling_pct ?? null;
      const liqFloor = c?.liquidity_t7_min_pct ?? null;

      const teStatus: RiskStatus = teCeiling != null ? riskStatus(te, teCeiling) : 'within';
      const mddStatus: RiskStatus = mddCeiling != null ? riskStatus(mdd, mddCeiling) : 'within';
      const liqStatus: RiskStatus = liqFloor != null ? minRiskStatus(liqCoverage, liqFloor) : 'within';

      return {
        level,
        profile,
        te, teCeiling, teStatus,
        mdd, mddCeiling, mddStatus,
        liqCoverage, liqFloor, liqStatus,
        unmappedWeight,
      };
    });
  }, [daily, ceilingByLevel, latestWeightsByLevel, fundIsinById, liquidityByIsin]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Risk Lenses — {range} Portfolios</CardTitle>
        <CardDescription>
          Tier 1 §11.1 secondary lenses: tracking error vs benchmark (annualised, 1yr), maximum historical drawdown, and 7-day liquidity coverage. Ceilings from §11.2 (editable in Compliance → Thresholds).
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Portfolio</TableHead>
              <TableHead>Risk Profile</TableHead>
              <TableHead className="text-right">Tracking Error (%)</TableHead>
              <TableHead className="text-right">TE Ceiling</TableHead>
              <TableHead>TE Status</TableHead>
              <TableHead className="text-right">Max Drawdown (%)</TableHead>
              <TableHead className="text-right">DD Ceiling</TableHead>
              <TableHead>DD Status</TableHead>
              <TableHead className="text-right">T+7 Liquidity (%)</TableHead>
              <TableHead className="text-right">Min Floor</TableHead>
              <TableHead>Liq Status</TableHead>
              <TableHead className="text-right">Unmapped wt.</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.level}>
                <TableCell className="font-medium">{range}{r.level}</TableCell>
                <TableCell>{r.profile}</TableCell>
                <TableCell className="text-right tabular-nums">{r.te != null ? r.te.toFixed(2) : '—'}</TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">{r.teCeiling != null ? r.teCeiling.toFixed(1) : '—'}</TableCell>
                <TableCell><StatusBadge status={r.teStatus} hasData={r.te != null} /></TableCell>
                <TableCell className="text-right tabular-nums">{r.mdd != null ? r.mdd.toFixed(2) : '—'}</TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">{r.mddCeiling != null ? r.mddCeiling.toFixed(1) : '—'}</TableCell>
                <TableCell><StatusBadge status={r.mddStatus} hasData={r.mdd != null} /></TableCell>
                <TableCell className="text-right tabular-nums">{r.liqCoverage != null ? r.liqCoverage.toFixed(1) : '—'}</TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">{r.liqFloor != null ? r.liqFloor.toFixed(0) : '—'}</TableCell>
                <TableCell><StatusBadge status={r.liqStatus} hasData={r.liqCoverage != null} /></TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">{r.unmappedWeight > 0.01 ? `${r.unmappedWeight.toFixed(1)}%` : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <p className="text-xs text-muted-foreground mt-3">
          Liquidity coverage = weight-averaged T+7 redeemable share across current holdings, mapped by ISIN to <code>risk_fund_liquidity</code>. Populate liquidity data in Compliance → Thresholds for full coverage. Unmapped weight is excluded from the average.
        </p>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status, hasData }: { status: RiskStatus; hasData: boolean }) {
  if (!hasData) return <Badge variant="outline" className="gap-1"><ShieldQuestion className="h-3 w-3" />No data</Badge>;
  if (status === 'breach') return <Badge variant="destructive" className="gap-1"><ShieldAlert className="h-3 w-3" />Breach</Badge>;
  if (status === 'amber') return <Badge className="gap-1 bg-amber-500 text-white hover:bg-amber-600"><ShieldAlert className="h-3 w-3" />Amber</Badge>;
  return <Badge variant="secondary" className="gap-1"><ShieldCheck className="h-3 w-3" />Within</Badge>;
}
